import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type { ApproachArcConfig } from 'types/approach';
import type { StageDefinition } from 'types/arc';
import TagAtom from '../../atoms/Tag/TagAtom';
import './ArcStagesMolecule.scss';

export interface ArcStagesMoleculeProps {
  arc: ApproachArcConfig;
  className?: string;
}

interface StageRowProps {
  stage: StageDefinition;
  index: number;
}

const StageRow: React.FC<StageRowProps> = ({ stage, index }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const intents = stage.intents ?? [];
  const transitions = stage.transition?.allowedTargets ?? [];
  const hasDetail = intents.length > 0 || transitions.length > 0;

  const isManual = stage.type === 'manual';
  const isTerminal = !!stage.isTerminal;
  const stageName = stage.fallbackName || stage.id;
  const indicatorLabel = isTerminal ? '✓' : String(index + 1);

  const indicatorClasses = [
    'marketplace-arc-stages__indicator',
    isTerminal
      ? 'marketplace-arc-stages__indicator--terminal'
      : index === 0
        ? 'marketplace-arc-stages__indicator--first'
        : 'marketplace-arc-stages__indicator--default',
  ].join(' ');

  const ariaLabel = open
    ? t('marketplace.approachDetail.whatsIncluded.arc.collapseStageAria', {
        name: stageName,
      })
    : t('marketplace.approachDetail.whatsIncluded.arc.expandStageAria', {
        name: stageName,
      });

  return (
    <li className="marketplace-arc-stages__stage">
      <button
        type="button"
        className="marketplace-arc-stages__stage-header"
        onClick={() => hasDetail && setOpen((v) => !v)}
        disabled={!hasDetail}
        aria-expanded={hasDetail ? open : undefined}
        aria-label={hasDetail ? ariaLabel : undefined}
      >
        <span className={indicatorClasses} aria-hidden="true">
          {indicatorLabel}
        </span>
        {stage.icon && (
          <i
            className={`pi pi-${stage.icon} marketplace-arc-stages__stage-icon`}
            aria-hidden="true"
          />
        )}
        <span className="marketplace-arc-stages__stage-name">{stageName}</span>
        <TagAtom
          tone={isManual ? 'outline' : 'info'}
          label={t(
            isManual
              ? 'marketplace.approachDetail.whatsIncluded.arc.manualBadge'
              : 'marketplace.approachDetail.whatsIncluded.arc.automatedBadge',
          )}
        />
        {isTerminal && (
          <TagAtom
            tone="success"
            label={t(
              'marketplace.approachDetail.whatsIncluded.arc.terminalBadge',
            )}
          />
        )}
        {hasDetail && (
          <i
            className={`pi ${
              open ? 'pi-chevron-up' : 'pi-chevron-down'
            } marketplace-arc-stages__chevron`}
            aria-hidden="true"
          />
        )}
      </button>

      {open && hasDetail && (
        <div className="marketplace-arc-stages__stage-detail">
          {intents.length > 0 && (
            <div className="marketplace-arc-stages__detail-block">
              <div className="marketplace-arc-stages__detail-label">
                {t('marketplace.approachDetail.whatsIncluded.arc.intentLabel')}
              </div>
              <div className="marketplace-arc-stages__detail-intents">
                {intents.map((intent) => intent.name).join(' · ')}
              </div>
            </div>
          )}
          {transitions.length > 0 && (
            <div className="marketplace-arc-stages__detail-block">
              <div className="marketplace-arc-stages__detail-label">
                {t(
                  'marketplace.approachDetail.whatsIncluded.arc.transitionsToLabel',
                )}
              </div>
              <div className="marketplace-arc-stages__detail-transitions">
                {transitions.join(', ')}
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const ArcStagesMolecule: React.FC<ArcStagesMoleculeProps> = ({
  arc,
  className = '',
}) => {
  const { t } = useTranslation();

  const stages: StageDefinition[] = useMemo(
    () => arc.inlineDefinition?.stages ?? [],
    [arc.inlineDefinition],
  );

  const keyDateNames = useMemo(() => {
    const inlineKeyDates = arc.inlineDefinition?.keyDates ?? {};
    return Object.keys(inlineKeyDates);
  }, [arc.inlineDefinition]);

  const sourceLabel = arc.inlineDefinition
    ? t('marketplace.approachDetail.whatsIncluded.arc.inlineLabel')
    : t('marketplace.approachDetail.whatsIncluded.arc.templateLabel', {
        name:
          arc.templateSystemName ||
          t('marketplace.approachDetail.whatsIncluded.arc.templateUnnamed'),
      });

  const rootClasses = ['marketplace-arc-stages', className].filter(Boolean).join(' ');

  return (
    <section className={rootClasses}>
      <header className="marketplace-arc-stages__header">
        <TagAtom tone="outline" label={sourceLabel} />
        {arc.isPrimary && (
          <TagAtom
            tone="accent"
            label={t(
              'marketplace.approachDetail.whatsIncluded.arc.primaryArc',
            )}
          />
        )}
      </header>

      {stages.length === 0 ? (
        <p className="marketplace-arc-stages__empty">
          {t('marketplace.approachDetail.whatsIncluded.arc.noStages')}
        </p>
      ) : (
        <ul className="marketplace-arc-stages__list">
          {stages.map((stage, idx) => (
            <StageRow key={stage.id} stage={stage} index={idx} />
          ))}
        </ul>
      )}

      {keyDateNames.length > 0 && (
        <div className="marketplace-arc-stages__key-dates">
          <div className="marketplace-arc-stages__detail-label">
            {t('marketplace.approachDetail.whatsIncluded.arc.keyDatesLabel')}
          </div>
          <div className="marketplace-arc-stages__key-date-list">
            {keyDateNames.map((name) => (
              <TagAtom key={name} tone="info" label={name} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

ArcStagesMolecule.displayName = 'ArcStagesMolecule';

export default ArcStagesMolecule;
